/**
 * Shared public-auth chrome so Sign In, Create Account, and Recover Password
 * use one card width, title, CTA, nav-link, and page shell.
 */

import React from 'react'
import { Box, Button, Grid, Link, Typography, useMediaQuery } from '@mui/material'
import SignInFooter from './SignInFooter'

export const AUTH_CARD_MAX_WIDTH = 480
export const AUTH_PRIMARY_COLOR = '#04a3e3'
export const AUTH_NAV_LINK_COLOR = '#3b3e66'

export const authBackdropSx = {
  color: '#fff',
  zIndex: 2000,
}

export const authLogoCenterSx = {
  textAlign: 'center',
}

export const authLogo40Sx = {
  height: 40,
}

export const authLogo50Sx = {
  height: 50,
}

export const authCardWrapSx = {
  p: '10px',
}

export const authCardSx = {
  maxWidth: AUTH_CARD_MAX_WIDTH,
  width: '100%',
}

export const authCardTitleSx = {
  fontWeight: 600,
}

export const authPrimaryButtonSx = {
  bgcolor: AUTH_PRIMARY_COLOR,
  color: 'white',
  textTransform: 'none',
}

export const authNavLinkSx = {
  color: AUTH_NAV_LINK_COLOR,
}

export const authFormSx = {
  mb: 0,
}

export const authFormErrorWrapSx = {
  mb: 2,
}

export const authFormErrorCardSx = {
  bgcolor: '#fdeaea',
  boxShadow: 'none',
  p: '8px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
}

export const authFormErrorIconSx = {
  color: '#d32f2f',
}

export const authFormErrorTextSx = {
  color: '#d32f2f',
}

export const authPasswordToggleAvatarSx = {
  bgcolor: 'transparent',
  color: '#929292',
}

export const authFlexSpacerSx = {
  flex: 1,
}

export const authTextEndSx = {
  textAlign: 'end',
}

export const authSnackbarCloseIconSx = {
  color: 'white',
}

export const authInlineLinkSx = {
  color: 'blue',
}

export const authResetSubmitSx = {
  bgcolor: AUTH_PRIMARY_COLOR,
  color: 'white',
}

const signInFooterBarLinkSx = {
  '& a.MuiLink-root': {
    color: '#cfcfcf !important',
    textDecoration: 'none',
    '&:hover': {
      color: '#ffffff !important',
      textDecoration: 'underline',
    },
    '&:visited': {
      color: '#cfcfcf !important',
    },
  },
}

export const signInFooterMobileBarSx = {
  bgcolor: '#253152',
  py: '32px',
  px: '24px',
  position: 'relative',
  mt: '25px',
  ...signInFooterBarLinkSx,
}

export const signInFooterDesktopBarSx = {
  bgcolor: '#253152',
  p: '50px',
  ...signInFooterBarLinkSx,
}

export const signInFooterInnerCenterSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
}

export const signInFooterColumnSx = {
  p: '20px',
  width: '100%',
}

export const signInFooterBrandStackSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  textAlign: 'center',
}

export const signInFooterLogoSx = {
  height: 40,
  display: 'block',
  m: 'auto',
}

export const signInFooterTaglineSx = {
  color: '#ffffff',
  textAlign: 'center',
}

export const signInFooterTaglineDesktopSx = {
  color: '#ffffff',
  pt: '20px',
}

export const signInFooterContactWrapSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}

export const signInFooterContactBtnSx = {
  color: '#1976d2',
}

export const signInFooterSectionTitleSx = {
  color: '#ffffff',
  whiteSpace: 'nowrap',
}

export const signInFooterMutedLinkSx = {
  color: '#cfcfcf !important',
  textDecoration: 'none',
  '&:hover': {
    color: '#ffffff !important',
    textDecoration: 'underline',
  },
  '&:visited': {
    color: '#cfcfcf !important',
  },
}

export const signInFooterCopyrightSx = {
  color: '#cfcfcf',
}

export const signInFooterCopyrightCenterSx = {
  color: '#cfcfcf',
  textAlign: 'center',
}

export const signInFooterSideColumnSx = {
  maxWidth: 300,
}

export const signInFooterLinksColumnSx = {
  pl: '100px',
  pr: '100px',
}

export const signInFooterCopyrightRowSx = {
  pt: '15px',
}

export function AuthPageShell({ children  }) {
  return (
    <Grid
      container
      direction="column"
      sx={{
        width: '100%',
        maxWidth: '100%',
        minHeight: '100vh',
      }}
    >
      <Grid
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          width: '100%',
          py: 4,
        }}
      >
        {children}
      </Grid>
      <SignInFooter />
    </Grid>
  )
}

export function AuthCardTitle({ children  }) {
  return (
    <Typography variant="h5" sx={authCardTitleSx}>
      {children}
    </Typography>
  )
}

export function AuthMobileLogo({ src }) {
  const hiddenSmUp = useMediaQuery((theme) => theme.breakpoints.up('sm'))
  if (hiddenSmUp || !src) return null
  return (
    <Grid sx={authLogoCenterSx}>
      <Box
        component="img"
        src={src}
        alt="Housefolios"
        sx={authLogo50Sx}
      />
    </Grid>
  )
}

export function AuthCardHeader({ title, subtitle, logoSrc }) {
  const hiddenSmDown = useMediaQuery((theme) =>
    theme.breakpoints.down('sm'),
  )
  return (
    <Grid
      container
      direction="column"
      spacing={2}
      sx={{
        alignItems: 'center',
      }}
    >
      {hiddenSmDown || !logoSrc ? null : (
        <Grid>
          <Box
            component="img"
            src={logoSrc}
            alt="Housefolios"
            sx={authLogo50Sx}
          />
        </Grid>
      )}
      <Grid>
        <AuthCardTitle>{title}</AuthCardTitle>
      </Grid>
      {subtitle ? (
        <Grid>
          <Typography>{subtitle}</Typography>
        </Grid>
      ) : null}
    </Grid>
  )
}

export function AuthPrimaryButton({ children, sx, ...props  }) {
  return (
    <Button
      variant="contained"
      fullWidth
      sx={[authPrimaryButtonSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      {...props}
    >
      <Typography>{children}</Typography>
    </Button>
  )
}

export function AuthNavLink({ children, sx, ...props  }) {
  return (
    <Link
      underline="hover"
      sx={[authNavLinkSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      {...props}
    >
      {children}
    </Link>
  )
}
